import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle, Lock } from 'lucide-react';
import { GenericCard } from '@/components/molecules/GenericCard';
import { Loader } from '@/components/atoms/Loader';
import { getAchievements, type AchievementWithStatus } from '@/services/achievement';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const rewardIcon = (type: AchievementWithStatus['reward']['type']): string =>
  type === 'pokeDollars' ? '🪙' : '🎁';

// ─── Carte succès ─────────────────────────────────────────────────────────────

const AchievementCard = ({ a }: { a: AchievementWithStatus }) => {
  const isSecret = a.hidden && !a.unlocked;
  const pct =
    a.progress && a.progress.max > 0
      ? Math.round((a.progress.current / a.progress.max) * 100)
      : null;

  return (
    <div
      className={`relative flex flex-col gap-3 rounded-2xl border p-4 transition-all ${
        a.unlocked ? 'border-yellow-500/40 bg-yellow-500/10 shadow-sm' : 'border-line-soft bg-surface'
      }`}
    >
      {a.unlocked && (
        <span className="absolute top-3 right-3">
          <CheckCircle size={18} className="text-yellow-500" />
        </span>
      )}

      {/* Image + titre */}
      <div className="flex items-center gap-3">
        <div
          className={`flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl ${
            a.unlocked ? 'bg-yellow-500/15' : 'bg-raised'
          }`}
        >
          {isSecret ? (
            <Lock size={24} className="text-muted" />
          ) : (
            <img
              src={a.imageUrl}
              alt={a.title}
              className={`h-12 w-12 object-contain ${a.unlocked ? '' : 'opacity-40 grayscale'}`}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p
            className={`font-lexend text-sm font-semibold leading-tight ${
              a.unlocked ? 'text-content' : 'text-content/50'
            }`}
          >
            {isSecret ? '???' : a.title}
          </p>
          {a.unlocked && a.unlockedAt && (
            <p className="mt-0.5 text-[11px] text-yellow-600">
              {new Date(a.unlockedAt).toLocaleDateString('fr-FR')}
            </p>
          )}
        </div>
      </div>

      {/* Description */}
      <p className={`text-xs leading-relaxed ${isSecret ? 'italic text-gray-300' : 'text-content/60'}`}>
        {isSecret ? 'Succès secret — à découvrir !' : a.description}
      </p>

      {/* Barre de progression (si non débloqué et quantifiable) */}
      {!a.unlocked && !isSecret && pct !== null && (
        <div>
          <div className="mb-1 flex items-center justify-between">
            <span className="text-[11px] text-muted">Progression</span>
            <span className="text-[11px] font-semibold text-soft">
              {a.progress!.current} / {a.progress!.max}
            </span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-line-soft">
            <div
              className="from-primary-500 to-secondary-500 h-2 rounded-full bg-linear-to-r transition-all duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      )}

      {/* Récompense */}
      {!isSecret && (
        <div className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold ${
          a.unlocked
            ? 'bg-yellow-500/15 text-yellow-700 dark:text-yellow-400'
            : 'bg-raised text-content/60'
        }`}>
          <span>{rewardIcon(a.reward.type)}</span>
          <span>{a.rewardLabel}</span>
        </div>
      )}
    </div>
  );
};

// ─── Vue principale ───────────────────────────────────────────────────────────

type Filter = 'all' | 'unlocked' | 'locked';

export const Achievements = () => {
  const [filter, setFilter] = useState<Filter>('all');

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['achievements'],
    queryFn: getAchievements,
    staleTime: 60_000,
  });

  const achievements = data ?? [];
  const unlockedCount = achievements.filter((a) => a.unlocked).length;
  // On ne compte pas les secrets cachés dans le total visible
  const visibleTotal = achievements.filter((a) => a.unlocked || !a.hidden).length;
  const globalPct = visibleTotal > 0 ? Math.round((unlockedCount / visibleTotal) * 100) : 0;

  const displayed = achievements.filter((a) => {
    if (filter === 'unlocked') return a.unlocked;
    if (filter === 'locked') return !a.unlocked;
    return true;
  });

  return (
    <main className="min-h-screen">
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 p-5">

        {/* Header progression globale */}
        <GenericCard
          title="Succès"
          subtitle={`${unlockedCount} / ${visibleTotal} succès débloqués`}
        >
          <div className="mt-2 mb-1">
            <div className="bg-primary-500/20 h-3 overflow-hidden rounded-full">
              <div
                className="h-3 rounded-full bg-linear-to-r from-yellow-400 to-yellow-600 transition-all duration-500"
                style={{ width: `${globalPct}%` }}
              />
            </div>
            <p className="text-content/60 mt-1.5 text-right text-xs font-semibold">
              {globalPct}%
            </p>
          </div>
        </GenericCard>

        {/* Liste */}
        <GenericCard title="Tous les succès">
          <div className="mb-4 flex gap-2">
            {(['all', 'unlocked', 'locked'] as Filter[]).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setFilter(v)}
                className={`h-9 rounded-xl px-4 text-sm font-semibold transition-colors ${
                  filter === v
                    ? 'bg-primary-500 text-white'
                    : 'text-content/60 border border-line-soft bg-raised hover:bg-line-soft'
                }`}
              >
                {v === 'all' ? 'Tous' : v === 'unlocked' ? 'Débloqués' : 'À débloquer'}
              </button>
            ))}
          </div>

          {isLoading ? (
            <div className="flex justify-center py-16">
              <Loader size={40} color="#f29602" />
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center gap-4 py-16">
              <p className="text-content/60 text-sm">Impossible de charger les succès.</p>
              <button
                onClick={() => refetch()}
                className="text-primary-500 hover:text-primary-600 text-sm font-semibold underline"
              >
                Réessayer
              </button>
            </div>
          ) : displayed.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-12">
              <p className="text-5xl">🏆</p>
              <p className="text-content/50 text-sm">Aucun succès dans cette catégorie.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {displayed.map((a) => (
                <AchievementCard key={a.id} a={a} />
              ))}
            </div>
          )}
        </GenericCard>
      </div>
    </main>
  );
};
